import {
  readConfig,
  getCustomerKey,
  getSessionKey,
  getTouchpoint,
  requestVoyadoJson,
  createProductCardRenderer,
  buildSwatchStyle,
  syncCustomerKey,
  sendClickNotification,
  escapeHtml,
  escapeAttr,
  safeUrl,
  formatPrice,
  hasVoyadoConsent,
} from 'voyado-elevate';

const _syncOnce = syncCustomerKey();
let _loadedWithoutConsent = false;

// window-level singleton — survives multiple module evaluations caused by
// Shopify injecting one <script> tag per section instance on the page.
// window.__voyadoPageRecsPromise holds the shared fetch promise.
// window.__voyadoPageRecsRunning prevents a second evaluation from re-collecting
// sections and firing a duplicate request before the first one resolves.

function getPageRecsMediaStyle(imageAspectRatio) {
  if (imageAspectRatio === 'square') return 'aspect-ratio: 1 / 1;';
  if (imageAspectRatio === 'portrait') return 'aspect-ratio: 3 / 4;';
  if (imageAspectRatio === 'landscape') return 'aspect-ratio: 4 / 3;';
  return '';
}

function getPageRecsImageHeight(imageAspectRatio) {
  if (imageAspectRatio === 'portrait') return 533;
  if (imageAspectRatio === 'landscape') return 300;
  return 400;
}

function readSectionConfig(element) {
  const data = element.dataset;

  return {
    sectionId: data.sectionId,
    pageId: data.pageId || '/',
    locale: data.voyadoLocale || '',
    limit: parseInt(data.limit || '8', 10) || 8,
    columnsDesktop: parseInt(data.columnsDesktop || '4', 10) || 4,
    columnsMobile: parseInt(data.columnsMobile || '2', 10) || 2,
    showImage: data.showImage !== 'false',
    imageAspectRatio: data.imageAspectRatio || 'natural',
    enableImageSlider: data.enableImageSlider === 'true',
    showPrice: data.showPrice !== 'false',
    showSwatch: true,
    layout: data.layout === 'slider' ? 'slider' : 'grid',
    headingLevel: ['h2', 'h3', 'h4'].includes(data.headingLevel) ? data.headingLevel : 'h3',
    sectionHeading: data.sectionHeading || '',
    sectionHeadingLevel: ['h2', 'h3', 'h4'].includes(data.sectionHeadingLevel) ? data.sectionHeadingLevel : 'h2',
    priceId: data.priceId || '',
    stores: data.stores || '',
    presentCustom: data.presentCustom || '',
    enableTab2: data.enableTabs === 'true',
    tab1Heading: data.tab1Heading || '',
    tab2Heading: data.tab2Heading || '',
  };
}

// Returns an ordered flat array of lists from a landing-page API response.
// The actual response has recommendationLists at the root. primaryList is a
// fallback for response shapes that nest them.
function extractOrderedLists(data) {
  if (Array.isArray(data?.recommendationLists) && data.recommendationLists.length > 0) {
    return data.recommendationLists;
  }

  const primaryList = data?.primaryList ?? {};
  const primaryProducts = Array.isArray(primaryList.productGroups) ? primaryList.productGroups : [];
  const recLists = Array.isArray(primaryList.recommendationLists) ? primaryList.recommendationLists : [];

  const combined = [];

  if (primaryProducts.length > 0) {
    combined.push({
      id: 'primary',
      label: primaryList.label || '',
      productGroups: primaryProducts,
      visible: true,
    });
  }

  recLists.forEach((list) => combined.push(list));

  return combined;
}

class VoyadoPageRecsSection {
  constructor(root, baseConfig) {
    this._root = root;
    this._sectionConfig = readSectionConfig(root);
    this._baseConfig = baseConfig;

    Object.assign(this, createProductCardRenderer(() => this._sectionConfig, {
      buildSwatchStyleFn: buildSwatchStyle,
      productInfoVariant: 'product',
      getMediaStyle: getPageRecsMediaStyle,
      getImageHeight: getPageRecsImageHeight,
    }));

    this._loading = document.getElementById(`voyado-page-recs-loading-${this._sectionConfig.sectionId}`);
    this._lists = document.getElementById(`voyado-page-recs-lists-${this._sectionConfig.sectionId}`);

    this._bindProductCardContainer(this._lists);

    this._lists2 = null;

    if (this._sectionConfig.enableTab2) {
      const sectionId = this._sectionConfig.sectionId;
      this._lists2 = document.getElementById(`voyado-page-recs-lists-tab2-${sectionId}`);
      if (this._lists2) this._bindProductCardContainer(this._lists2);
      this._bindTabButtons();
    }
  }

  get listSlots() {
    return this._sectionConfig.enableTab2 ? 2 : 1;
  }

  get pageId() {
    return this._sectionConfig.pageId;
  }

  get locale() {
    return this._sectionConfig.locale;
  }

  get priceId() {
    return this._sectionConfig.priceId;
  }

  get stores() {
    return this._sectionConfig.stores;
  }

  get presentCustom() {
    return this._sectionConfig.presentCustom;
  }

  showLoading() {
    if (this._loading) {
      this._root.hidden = false;
      this._loading.hidden = false;
    }

    if (this._lists) {
      this._lists.hidden = true;
      this._lists.innerHTML = '';
    }

    if (this._lists2) {
      this._lists2.hidden = true;
      this._lists2.innerHTML = '';
    }
  }

  hideSection() {
    if (this._lists) {
      this._lists.hidden = true;
      this._lists.innerHTML = '';
    }

    if (this._loading) {
      this._loading.hidden = true;
    }

    this._root.hidden = true;
  }

  render(list) {
    if (this._loading) this._loading.hidden = true;

    if (!this._lists) {
      this.hideSection();
      return;
    }

    if (!list || list.visible === false) {
      this.hideSection();
      return;
    }

    const productGroups = this._normalizeProductGroups(list?.productGroups ?? list?.products ?? []);
    if (productGroups.length === 0) {
      this.hideSection();
      return;
    }

    const cardsHtml = productGroups
      .map((pg) => this._renderProductCard(pg))
      .filter(Boolean)
      .join('');

    if (!cardsHtml) {
      this.hideSection();
      return;
    }

    const isSlider = this._sectionConfig.layout === 'slider';

    const listHtml = `
      <section class="voyado-rec__list">
        ${isSlider ? this._renderSliderInner(cardsHtml) : this._renderGridInner(cardsHtml)}
      </section>
    `;

    const sectionHeading = this._sectionConfig.sectionHeading.trim();
    const sectionHeadingTag = this._sectionConfig.sectionHeadingLevel;

    this._lists.innerHTML = `
      ${sectionHeading ? `<div class="prose voyado-rec__section-heading"><${sectionHeadingTag}>${escapeHtml(sectionHeading)}</${sectionHeadingTag}></div>` : ''}
      ${listHtml}
    `;
    this._lists.hidden = false;
    this._root.hidden = false;
  }

  renderTab2(list) {
    if (!this._lists2) return;

    if (!list || list.visible === false) {
      this._lists2.innerHTML = '';
      return;
    }

    const productGroups = this._normalizeProductGroups(list?.productGroups ?? list?.products ?? []);
    if (productGroups.length === 0) {
      this._lists2.innerHTML = '';
      return;
    }

    const cardsHtml = productGroups
      .map((pg) => this._renderProductCard(pg))
      .filter(Boolean)
      .join('');

    if (!cardsHtml) {
      this._lists2.innerHTML = '';
      return;
    }

    const isSlider = this._sectionConfig.layout === 'slider';
    const sectionHeading = this._sectionConfig.sectionHeading.trim();
    const sectionHeadingTag = this._sectionConfig.sectionHeadingLevel;

    this._lists2.innerHTML = `
      ${sectionHeading ? `<div class="prose voyado-rec__section-heading"><${sectionHeadingTag}>${escapeHtml(sectionHeading)}</${sectionHeadingTag}></div>` : ''}
      <section class="voyado-rec__list">
        ${isSlider ? this._renderSliderInner(cardsHtml, '-tab2') : this._renderGridInner(cardsHtml)}
      </section>
    `;
    this._lists2.hidden = false;
  }

  _bindTabButtons() {
    const buttons = Array.from(this._root.querySelectorAll('[role="tablist"] [role="tab"]'));
    const panels = Array.from(this._root.querySelectorAll('[data-voyado-tab-panel]'));
    if (buttons.length < 2) return;
    buttons.forEach((btn, i) => {
      btn.addEventListener('click', () => {
        buttons.forEach((b, j) => b.setAttribute('aria-selected', i === j ? 'true' : 'false'));
        panels.forEach((p, j) => { p.hidden = (i !== j); });
      });
    });
  }

  _renderGridInner(cardsHtml) {
    const cols = escapeAttr(String(this._sectionConfig.columnsDesktop));
    const colsMobile = escapeAttr(String(this._sectionConfig.columnsMobile));
    return `<div class="voyado-search__grid" style="--columns-desktop:${cols};--columns-mobile:${colsMobile}">${cardsHtml}</div>`;
  }

  _renderSliderInner(cardsHtml, idSuffix = '') {
    const sliderId = escapeAttr(`voyado-page-recs-slider-${this._sectionConfig.sectionId}${idSuffix}`);
    return `
      <div class="floating-controls-container floating-controls-container--inside floating-controls-container--on-hover">
        <scroll-carousel id="${sliderId}" group-cells allow-drag class="product-list product-list--carousel scroll-area bleed md:unbleed" style="--product-list-items-per-row:${this._sectionConfig.columnsDesktop}">
          ${cardsHtml}
        </scroll-carousel>
        <button is="carousel-prev-button" type="button" class="prev-next-button prev-next-button--prev circle-button hover:animate-icon-inline" aria-controls="${sliderId}" disabled>
          <span class="sr-only">Zurück</span>
          <svg aria-hidden="true" focusable="false" fill="none" width="16" viewBox="0 0 16 18"><path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/></svg>
        </button>
        <button is="carousel-next-button" type="button" class="prev-next-button prev-next-button--next circle-button hover:animate-icon-inline" aria-controls="${sliderId}">
          <span class="sr-only">Weiter</span>
          <svg aria-hidden="true" focusable="false" fill="none" width="16" viewBox="0 0 16 18"><path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/></svg>
        </button>
      </div>
    `;
  }

  _canRenderSwatches() {
    return this._sectionConfig.showSwatch && this._baseConfig.showSwatches;
  }
}

async function initializeVoyadoPageRecs() {
  if (window.__voyadoPageRecsRunning) return;
  window.__voyadoPageRecsRunning = true;

  const elements = document.querySelectorAll('[data-voyado-page-recs]');
  if (elements.length === 0) return;

  const baseConfig = readConfig();

  const sections = Array.from(elements)
    .filter((el) => el instanceof HTMLElement)
    .map((el) => {
      try {
        return new VoyadoPageRecsSection(el, baseConfig);
      } catch {
        el.hidden = true;
        return null;
      }
    })
    .filter(Boolean);

  if (sections.length === 0) return;

  sections.forEach((s) => s.showLoading());

  await _syncOnce;

  if (!window.__voyadoPageRecsPromise) {
    const firstSection = sections[0];
    const locale = firstSection.locale || baseConfig.locale;

    const params = {
      pageReference: firstSection.pageId,
      customerKey: getCustomerKey(),
      sessionKey: getSessionKey(),
      market: baseConfig.market,
      locale,
      touchpoint: getTouchpoint(),
    };

    if (firstSection.priceId) params.priceId = firstSection.priceId;
    if (firstSection.stores) params.stores = firstSection.stores;
    if (firstSection.presentCustom) params.presentCustom = firstSection.presentCustom;

    window.__voyadoPageRecsPromise = requestVoyadoJson(
      baseConfig.cluster,
      '/api/storefront/v3/queries/landing-page',
      params,
    );
  }

  try {
    const data = await window.__voyadoPageRecsPromise;

    // Track whether this load ran without consent so we can re-fetch when consent arrives
    if (!hasVoyadoConsent()) {
      _loadedWithoutConsent = true;
    }

    const lists = extractOrderedLists(data);

    let listIndex = 0;
    sections.forEach((section) => {
      section.render(lists[listIndex] ?? null);
      if (section._sectionConfig.enableTab2) {
        section.renderTab2(lists[listIndex + 1] ?? null);
        listIndex += 2;
      } else {
        listIndex += 1;
      }
    });
  } catch {
    sections.forEach((s) => s.hideSection());
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeVoyadoPageRecs, { once: true });
} else {
  initializeVoyadoPageRecs();
}

// Re-fetch page recommendations when the visitor accepts cookies after the initial load
document.addEventListener('visitorConsentCollected', () => {
  if (!_loadedWithoutConsent) return;
  _loadedWithoutConsent = false;
  window.__voyadoPageRecsRunning = false;
  window.__voyadoPageRecsPromise = null;
  initializeVoyadoPageRecs();
}, { once: true });
